import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useAppContext } from "./AppContext";
import { ampli } from "@web/lib/ampli";
const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY as string;

interface AmplitudeContextType {
  ampli: typeof ampli;
  isInitialized: boolean;
}

const AmplitudeContext = createContext<AmplitudeContextType | null>(null);

interface AmplitudeProviderProps {
  children: ReactNode;
}

export function AmplitudeProvider({ children }: AmplitudeProviderProps) {
  const { session } = useAppContext();

  const value = useMemo<AmplitudeContextType>(
    () => ({
      ampli: ampli,
      isInitialized: !!AMPLITUDE_API_KEY,
    }),
    [],
  );

  useEffect(() => {
    if (!AMPLITUDE_API_KEY) {
      console.warn("Amplitude API key is not set");
      return;
    }

    ampli.load({
      client: {
        apiKey: AMPLITUDE_API_KEY,
        configuration: {
          autocapture: {
            attribution: true,
            formInteractions: false,
            pageViews: true,
            sessions: true,
            elementInteractions: false,
            networkTracking: false,
          },
        },
      },
    });
    
    console.log("Ampli SDK initialized.");
  }, []);

  useEffect(() => {
    if (!AMPLITUDE_API_KEY || !ampli.isLoaded) {
      return;
    }

    const userId = session?.user.id;
    if (userId) {
      ampli.identify(userId);
      console.log(`User identified: ${userId}`);
    }
  }, [session?.user.id]);

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  );
}

export function useAmplitude() {
  const context = useContext(AmplitudeContext);
  if (!context) {
    throw new Error(
      "useAmplitude must be used within an AmplitudeProvider",
    );
  }
  return context;
}